import { useCallback } from 'react';

import { buildGroupAwareRects, expandGroupPositions } from '../../../utils/group/nodeGroupAlignment';
import { type NodeRect, toNodeRect } from '../../../utils/layout/nodeLayout';
import { useGetSelectedNodes } from '../../useGetSelectedNodes';
import { commands } from '../commands';
import { type NodePosition } from '../types';
import { useCommandHistory } from './useCommandHistory';

/**
 * Returns a callback that runs the standard group-aware layout pipeline:
 * get selected nodes → build group-aware rects → run layout function → expand back → execute move command.
 *
 * Callers pass a `layoutFn` that receives the layout rects and returns new positions,
 * plus an optional `minCount` (defaults to 2).
 */
export function useLayoutCommandExecutor() {
  const execute = useCommandHistory((state) => state.execute);
  const getSelectedNodes = useGetSelectedNodes();

  return useCallback(
    (layoutFn: (rects: NodeRect[]) => NodePosition[], minCount = 2) => {
      const selectedNodes = getSelectedNodes();
      const { layoutRects, memberMap } = buildGroupAwareRects(selectedNodes);

      if (layoutRects.length < minCount) {
        return;
      }

      const previousPositions = selectedNodes.map(toNodeRect).map((r) => ({ id: r.id, position: { x: r.x, y: r.y } }));
      const layoutPositions = layoutFn(layoutRects);
      const newPositions = expandGroupPositions(layoutPositions, layoutRects, memberMap);

      execute(commands.moveNodes(previousPositions, newPositions));
    },
    [execute, getSelectedNodes],
  );
}
